import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';

// Async thunk for fetching credentials status
export const fetchCredentialsStatus = createAsyncThunk(
  'credentials/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.settings.getCredentialsStatus();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch credentials status');
    }
  }
);

const initialState = {
  // Credentials data
  configured: false,
  clientId: null,
  onbehalfof: null, // TIN value
  hasOnbehalfof: false,
  environment: 'sandbox',
  updatedAt: null,
  
  // Loading states
  loading: false,
  error: null,
  lastFetched: null,
};

const credentialsSlice = createSlice({
  name: 'credentials',
  initialState,
  reducers: {
    // Clear credentials state (e.g., on logout)
    clearCredentials: (state) => {
      state.configured = false;
      state.clientId = null;
      state.onbehalfof = null;
      state.hasOnbehalfof = false;
      state.environment = 'sandbox';
      state.updatedAt = null;
      state.error = null;
      state.lastFetched = null;
    },
    
    // Update credentials from external source (e.g., after saving credentials)
    updateCredentials: (state, action) => {
      const { configured, client_id, onbehalfof, has_onbehalfof, environment, updatedAt } = action.payload;
      state.configured = configured || false;
      state.clientId = client_id || null;
      state.onbehalfof = onbehalfof || null;
      state.hasOnbehalfof = has_onbehalfof || !!onbehalfof;
      state.environment = environment || 'sandbox';
      state.updatedAt = updatedAt || null;
      state.lastFetched = Date.now();
    },
    
    // Clear error state
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCredentialsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCredentialsStatus.fulfilled, (state, action) => {
        state.loading = false;
        
        if (action.payload.status === 'success' && action.payload.data) {
          const data = action.payload.data;
          
          // Update all credentials fields
          state.configured = data.configured || false;
          state.clientId = data.client_id || null;
          state.onbehalfof = data.onbehalfof || null;
          state.hasOnbehalfof = data.has_onbehalfof || !!data.onbehalfof;
          state.environment = data.environment || 'sandbox';
          state.updatedAt = data.updatedAt || null;
          
          // Update metadata
          state.lastFetched = Date.now();
          state.error = null;
        }
      })
      .addCase(fetchCredentialsStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch credentials status';
      });
  },
});

// Selectors
export const selectCredentials = (state) => state.credentials;
export const selectOnbehalfof = (state) => state.credentials.onbehalfof;
export const selectCredentialsLoading = (state) => state.credentials.loading;
export const selectCredentialsError = (state) => state.credentials.error;
export const selectCredentialsConfigured = (state) => state.credentials.configured;

// Export actions
export const { clearCredentials, updateCredentials, clearError } = credentialsSlice.actions;

export default credentialsSlice.reducer;
